// / (a raiz de admin.plugchef.com.br) — entrada da EQUIPE RESULTI, com autenticador (TOTP) obrigatório.
//
// Três etapas, conduzidas pela resposta da API:
//   1. e-mail + senha;
//   2a. 1º acesso (TOTP ainda não ativo): a API devolve `CONFIGURAR_TOTP` com o segredo e o
//       `otpauth://` → mostramos o QR e o segredo em blocos; o 1º código vai para /auth/totp/ativar
//       com o desafio (10 min; vencido = 401 DESAFIO_INVALIDO, volta ao começo), que abre a sessão;
//   2b. TOTP já ativo: o login sem código volta 401 `TOTP_NECESSARIO` → pedimos o código e
//       reenviamos e-mail + senha + código.
// O mesmo código não serve duas vezes (`TOTP_REPETIDO`): quem o viu por cima do ombro não entra.
//
// O visual é o da tela de entrada do PlugChef Desktop (o mesmo do login do cliente), com a faixa
// âmbar da Área Resulti em cima — ela aparece antes mesmo do login, para ninguém confundir as áreas.
import { useState, type FormEvent } from 'react';
import { ErroApi, mensagemDeErro } from '../../api/client';
import { ativarTotp, entrar as entrarNaApi } from '../../api/admin';
import type { RespostaLogin } from '../../api/tipos';
import { BotaoCopiar, TelaCarregando } from '../../components/base';
import {
  AvisoEntrada, BotaoEntrada, CampoEntrada, CampoSenha, CLASSE_LINK_ENTRADA,
} from '../../components/desktop/CampoEntrada';
import { IconClock, IconUser } from '../../components/desktop/icones';
import { QrCode } from '../../components/QrCode';
import { corpoEntrada, FaixaResulti, TelaLogin } from '../../components/TelaLogin';
import { emBlocos } from '../../lib/formato';
import { destinoSeguro, Redirecionar, useLocalizacao } from '../../lib/rotas';
import { useTitulo } from '../../lib/useTitulo';
import { useSessao } from '../../store/sessao';
import { ehTelaDoConsole, INICIO } from '../../components/console/menu';

type Etapa =
  | { tipo: 'CREDENCIAIS' }
  | { tipo: 'CODIGO' }
  | { tipo: 'CONFIGURAR'; desafio: string; segredo: string; otpauthUrl: string };

/** Só os dígitos, no máximo 6 — o app autenticador mostra "123 456" e a pessoa cola com espaço. */
function soDigitos(v: string): string {
  return v.replace(/\D/g, '').slice(0, 6);
}

export function PaginaLoginResulti(): JSX.Element {
  useTitulo('Área Resulti');
  // `aviso`: a saída que o próprio portal pediu (a senha trocada derruba todas as sessões).
  const { situacao, usuario, entrar, aviso } = useSessao();
  const { busca } = useLocalizacao();
  const [etapa, setEtapa] = useState<Etapa>({ tipo: 'CREDENCIAIS' });
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (situacao === 'verificando') return <TelaCarregando />;
  if (situacao === 'ativa' && usuario) return <Redirecionar para={destinoSeguro(busca.get('volta'), ehTelaDoConsole, INICIO)} />;

  function tratarResposta(r: RespostaLogin): void {
    if (r.etapa === 'SESSAO') {
      entrar(r.usuario, r.acesso); // a sessão ativa redireciona (acima)
      return;
    }
    setCodigo('');
    setEtapa({ tipo: 'CONFIGURAR', desafio: r.desafio, segredo: r.segredo, otpauthUrl: r.otpauthUrl });
    setEnviando(false);
  }

  function tratarErro(e: unknown): void {
    setEnviando(false);
    const codigoErro = e instanceof ErroApi ? e.codigo : undefined;
    if (codigoErro === 'TOTP_NECESSARIO') {
      // Senha certa, falta o código: não é erro para a pessoa, é a próxima etapa.
      setErro(null);
      setCodigo('');
      setEtapa({ tipo: 'CODIGO' });
      return;
    }
    if (codigoErro === 'CREDENCIAIS_INVALIDAS' || codigoErro === 'DESAFIO_INVALIDO') {
      // Volta ao começo: a senha mudou, a conta foi inativada ou os 10 min de configuração acabaram.
      setEtapa({ tipo: 'CREDENCIAIS' });
      setSenha('');
      setCodigo('');
    } else if (codigoErro === 'TOTP_INVALIDO' || codigoErro === 'TOTP_REPETIDO') {
      setCodigo('');
    }
    setErro(mensagemDeErro(e, 'Não foi possível entrar.'));
  }

  async function enviarCredenciais(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      tratarResposta(await entrarNaApi(email.trim(), senha));
    } catch (err) {
      tratarErro(err);
    }
  }

  async function enviarCodigo(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (codigo.length !== 6) {
      setErro('O código tem 6 números.');
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      if (etapa.tipo === 'CONFIGURAR') tratarResposta(await ativarTotp(etapa.desafio, codigo));
      else tratarResposta(await entrarNaApi(email.trim(), senha, codigo));
    } catch (err) {
      tratarErro(err);
    }
  }

  function recomecar(): void {
    setEtapa({ tipo: 'CREDENCIAIS' });
    setCodigo('');
    setSenha('');
    setErro(null);
  }

  const campoCodigo = (
    <CampoEntrada
      icone={IconClock}
      rotulo="Código do autenticador"
      placeholder="000000"
      dica="Os 6 números que o aplicativo mostra agora (mudam a cada 30 segundos)."
      classeInput="num !pr-11 text-center text-xl font-bold tracking-[0.4em]"
      value={codigo}
      onChange={(e) => setCodigo(soDigitos(e.target.value))}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      required
      autoFocus
    />
  );

  return (
    <TelaLogin area="Área Resulti" subtitulo="Acesso interno — só para a equipe Resulti." faixa={<FaixaResulti />}>
      {etapa.tipo === 'CREDENCIAIS' ? (
        <form onSubmit={enviarCredenciais} className={corpoEntrada}>
          {aviso && !erro ? <AvisoEntrada tipo="ok">{aviso}</AvisoEntrada> : null}
          <CampoEntrada
            icone={IconUser}
            rotulo="E-mail"
            placeholder="Entre com seu e-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            autoComplete="username"
          />
          <CampoSenha
            rotulo="Senha"
            placeholder="Digite sua senha..."
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            autoComplete="current-password"
          />
          <AvisoEntrada>{erro}</AvisoEntrada>
          <BotaoEntrada disabled={enviando}>{enviando ? 'Conferindo…' : 'Continuar'}</BotaoEntrada>
        </form>
      ) : etapa.tipo === 'CODIGO' ? (
        <form onSubmit={enviarCodigo} className={corpoEntrada}>
          <div className="text-center">
            <p className="text-[14px] leading-relaxed text-tx">
              Abra o aplicativo autenticador do seu celular e digite o código de <strong>PlugChef Admin</strong>.
            </p>
            <div className="mt-1 break-all text-[12px] text-mut">{email}</div>
          </div>
          {campoCodigo}
          <AvisoEntrada>{erro}</AvisoEntrada>
          <BotaoEntrada disabled={enviando || codigo.length !== 6}>{enviando ? 'Conferindo…' : 'Entrar'}</BotaoEntrada>
          <button type="button" className={`mt-1 self-center ${CLASSE_LINK_ENTRADA}`} onClick={recomecar}>
            Usar outra conta
          </button>
        </form>
      ) : (
        <form onSubmit={enviarCodigo} className={corpoEntrada}>
          <div className="text-center">
            <div className="text-[15px] font-bold">Configure o autenticador (só desta vez)</div>
            <p className="mt-1 text-[13px] leading-relaxed text-mut">
              A Área Resulti exige um segundo fator. No celular, abra o Google Authenticator, o Microsoft Authenticator
              ou outro aplicativo de código e leia o QR abaixo.
            </p>
          </div>
          <div className="flex justify-center">
            <QrCode texto={etapa.otpauthUrl} tamanho={208} rotulo="QR code para o aplicativo autenticador" />
          </div>
          <div>
            <div className="text-[13px] font-bold text-mut">Sem câmera? Digite este segredo</div>
            <div className="mt-1.5 flex items-center gap-2">
              <code className="num min-w-0 flex-1 select-all break-all rounded-xl border border-bd bg-inputBg px-3 py-2.5 text-sm font-bold tracking-wider">
                {emBlocos(etapa.segredo)}
              </code>
              <BotaoCopiar texto={etapa.segredo} />
            </div>
            <p className="mt-1.5 text-[12px] text-mut2">Tipo: baseado em tempo · 6 dígitos · 30 segundos.</p>
          </div>
          {campoCodigo}
          <AvisoEntrada>{erro}</AvisoEntrada>
          <BotaoEntrada disabled={enviando || codigo.length !== 6}>{enviando ? 'Conferindo…' : 'Ativar e entrar'}</BotaoEntrada>
          <p className="text-center text-[12px] leading-relaxed text-mut2">
            Você tem 10 minutos para concluir. Perdeu o celular depois? Um administrador zera o seu autenticador em
            Equipe.
          </p>
          <button type="button" className={`self-center ${CLASSE_LINK_ENTRADA}`} onClick={recomecar}>
            Cancelar
          </button>
        </form>
      )}
    </TelaLogin>
  );
}
